import Box from '@material-ui/core/Box'
import { makeStyles } from '@material-ui/core/styles/'
import React, {
  lazy,
  Suspense,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react'
import { Settings } from 'react-slick'

import { createSlickArrows } from '../../../../Components/SlickArrows'
import colorContext from '../../../../Context/colorContext'
import useSlickA11y from '../../../../hooks/useSlickA11y'
import { BannerProps } from '../..'

const Slider = lazy(() => import('react-slick'))

interface Props {
  banners: BannerProps[]
}

const Banners = ({ banners }: Props): JSX.Element => {
  const { colors } = useContext(colorContext)
  const sliderRef = useRef<HTMLDivElement>(null)
  useSlickA11y(sliderRef, banners.length > 1)
  const useStyles = makeStyles(() => ({
    main: {
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      backgroundColor: colors.colorBackground,
      alignItems: 'center',
      padding: '50px 5px',
    },
    bannerMain: {
      width: '100%',
      maxWidth: 1320,
      height: 'auto',
      borderRadius: 4,
      maxHeight: 382,
      backgroundColor: 'rgba(247,250,255)',
    },
    textTitleInitial: {
      position: 'relative',
      textTransform: 'uppercase',
      fontFamily: 'Roboto',
      padding: '10px 0px 0px 3px',
      marginBottom: 5,
      fontWeight: 'bold',
      color: colors.textBody,
      left: 8,
      '&::after': {
        content: '""',
        display: 'block',
        width: 4,
        height: 24,
        backgroundColor: colors.textBody,
        position: 'absolute',
        bottom: 6,
        marginLeft: -14,
        left: 5,
      },
    },
    textSubTitle: {
      position: 'relative',
      fontFamily: 'Roboto',
      paddingBottom: 10,
      color: colors.subTitleColor,
      letterSpacing: '0.1px',
      lineHeight: '24px',
      fontWeight: 400,
      fontSize: 18,
    },
    boxImage: {
      height: '100%',
      maxHeight: 382,
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    legend: {
      position: 'absolute',
      zIndex: 9999,
      top: '70%',
      left: 0,
      right: 0,
      backgroundColor: 'rgba(11, 11, 11, 0.36)',
      padding: 10,
    },
    textLegend: {
      textTransform: 'capitalize',
      textDecoration: 'none',
      color: colors.textAccentColor,
      fontFamily: 'Roboto',
      fontSize: 16,
      fontWeight: 600,
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  }))
  const classes = useStyles()
  const { NextArrow, PrevArrow } = useMemo(
    () =>
      createSlickArrows({
        nextLabel: 'Próximo slide',
        prevLabel: 'Slide anterior',
      }),
    [],
  )

  const settings: Settings = {
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    lazyLoad: 'ondemand',
  }

  const renderContent = useCallback(() => {
    if (banners?.length === 1) {
      return (
        <>
          {banners.length > 0 &&
            banners?.map((banner) => (
              <Box key={`${banner?.titulo}-key`} className={classes.bannerMain}>
                <a
                  href={banner.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${banner.titulo} (abre em nova aba)`}
                >
                  <img
                    width="100%"
                    height="100%"
                    src={banner.url_imagem}
                    alt={banner.titulo}
                    loading="lazy"
                  />
                </a>
              </Box>
            ))}
        </>
      )
    }
    return (
      <Suspense fallback="">
        <div
          ref={sliderRef}
          style={{ width: '100%' }}
          role="region"
          aria-roledescription="carrossel"
          aria-label="Banners em destaque"
        >
          <Slider {...settings}>
            {banners.length > 0 &&
              banners?.map((banner) => (
                <Box
                  key={`${banner?.titulo}-key`}
                  className={classes.bannerMain}
                >
                  <a
                    href={banner.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${banner.titulo} (abre em nova aba)`}
                  >
                    <img
                      width="100%"
                      height="100%"
                      src={banner.url_imagem}
                      alt={banner.titulo}
                      loading="lazy"
                    />
                  </a>
                </Box>
              ))}
          </Slider>
        </div>
      </Suspense>
    )
  }, [banners, classes, settings])

  return (
    <Box className={classes.main}>
      <Box
        display="flex"
        maxWidth="1320px"
        width="100%"
        flexDirection="column"
        gridGap={30}
      >
        {renderContent()}
      </Box>
    </Box>
  )
}

export default Banners
